import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Plus, Trash2, Shield, Eye } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/lib/auth";
import PermissionEditorDialog from "./PermissionEditorDialog";

const ROLE_LABELS = { admin: "Administrator", manager: "Manager", agent: "Agent" };
const ROLE_COLORS = { admin: "text-primary", manager: "text-blue-500", agent: "text-emerald-500" };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "agent" });
  const [permEditOpen, setPermEditOpen] = useState(false);
  const [permEditUser, setPermEditUser] = useState(null);
  const { enterPreview, user: currentUser } = useAuth();
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setUsers(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("Completează toate câmpurile");
    try {
      await api.post("/users", form);
      toast.success("Utilizator creat");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "agent" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (u) => {
    if (!confirm(`Ștergi utilizatorul "${u.email}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Utilizator șters");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (u, active) => {
    try {
      await api.patch(`/users/${u.id}`, { active });
      toast.success(active ? "Utilizator activat" : "Utilizator dezactivat");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success("Rol actualizat");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const previewAs = (u) => {
    enterPreview(u);
    toast.success(`Preview ca ${u.name} · navighează pentru a vedea UI-ul lui`, { duration: 4000 });
    navigate("/");
  };

  const openPermEditor = (u) => {
    setPermEditUser(u);
    setPermEditOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold mb-2">Setări</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Utilizatori & roluri</h1>
          <p className="text-muted-foreground text-sm mt-1">Configurează cine vede ce. RBAC enterprise: rol = preset, permisiuni per user override.</p>
        </div>
        <Button onClick={() => setOpen(true)} data-testid="add-user-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Utilizator nou
        </Button>
      </div>

      <Card className="bg-card border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-secondary/40 border-b border-border">
            <tr className="text-left text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">
              <th className="px-4 py-3">Utilizator</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Rol</th>
              <th className="px-4 py-3">Permisiuni</th>
              <th className="px-4 py-3">Activ</th>
              <th className="px-4 py-3">Creat</th>
              <th className="px-4 py-3 text-right">Acțiuni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading ? (
              <tr><td colSpan={7} className="px-4 py-6 text-muted-foreground">Se încarcă…</td></tr>
            ) : users.map((u) => {
              const overrides = (u.added_permissions?.length || 0) + (u.removed_permissions?.length || 0);
              const isSelf = u.id === currentUser?.id;
              return (
                <tr key={u.id} className="hover:bg-secondary/40" data-testid={`user-row-${u.email}`}>
                  <td className="px-4 py-3 flex items-center gap-3">
                    <Avatar className="h-7 w-7">
                      <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                        {(u.name || "?").split(" ").map((p) => p[0]).join("").slice(0, 2)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="font-medium">{u.name}</span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3">
                    {isSelf ? (
                      <Badge variant="outline" className={`${ROLE_COLORS[u.role]} border-current/20`}>{ROLE_LABELS[u.role]}</Badge>
                    ) : (
                      <Select value={u.role} onValueChange={(v) => updateRole(u, v)}>
                        <SelectTrigger className="h-7 w-32 text-xs" data-testid={`role-select-${u.email}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="admin">Administrator</SelectItem>
                          <SelectItem value="manager">Manager</SelectItem>
                          <SelectItem value="agent">Agent</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs tabular-nums">{u.permissions?.length || 0}</span>
                      {overrides > 0 && (
                        <Badge variant="outline" className="text-[10px] border-amber-200 text-amber-700 bg-amber-50">
                          {overrides} override-uri
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <Switch
                      checked={u.active !== false}
                      onCheckedChange={(v) => toggleActive(u, v)}
                      disabled={isSelf}
                      data-testid={`active-switch-${u.email}`}
                    />
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => openPermEditor(u)} data-testid={`edit-perms-${u.email}`}>
                        <Shield className="h-3.5 w-3.5 mr-1" /> Permisiuni
                      </Button>
                      {!isSelf && (
                        <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={() => previewAs(u)} data-testid={`preview-as-${u.email}`} title="Vezi UI-ul așa cum îl vede acest user">
                          <Eye className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {!isSelf && (
                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-500" onClick={() => del(u)} data-testid={`delete-user-${u.email}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Utilizator nou</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Nume complet</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-user-name" /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="new-user-email" /></div>
            <div className="space-y-2"><Label>Parolă</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="new-user-password" /></div>
            <div className="space-y-2">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="new-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Administrator</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Anulează</Button>
            <Button onClick={create} data-testid="save-user-btn">Creează</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <PermissionEditorDialog
        open={permEditOpen}
        onOpenChange={setPermEditOpen}
        user={permEditUser}
        onSaved={load}
      />
    </div>
  );
}
